// Links between Me and the workspaces.
//
// Promise:
// - A link to the other place (`open(href, place, state)`) moves this window
//   there, with what the URL does not carry (Me's label) in the history state.
//   Me and a workspace side by side is something a person may do (Cmd or Ctrl
//   on a link), not something the screen steers them into.
// - Links opened with Cmd, Ctrl, Shift or the middle button are plain new tabs;
//   callers leave those to the browser.

export interface Windows {
  /** Moves this window to `href` (in Me or a workspace). */
  open(href: string, place: string, state?: Record<string, unknown>): boolean;
}

export function createWindows(deps: {
  /** Moves this window's router. */
  navigate(href: string, state?: Record<string, unknown>): void;
}): Windows {
  return {
    open(href, _place, state) {
      deps.navigate(href, state);
      return true;
    },
  };
}

/** Moves a router to `href` (the state goes into history). */
export const navigateRouter =
  (
    router: () => {
      navigate(options: { href: string; state?: never }): Promise<unknown>;
    },
  ) =>
  (href: string, state?: Record<string, unknown>) =>
    void router().navigate({
      href,
      ...(state ? { state: state as never } : {}),
    });

/** True when the click asks the browser for a tab of its own. */
export const wantsOwnTab = (e: {
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  button: number;
}) => e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0;
